import { mergeConversationRefresh } from "./conversationRefresh";
import { firstNonBlankText } from './contactDisplayName';
import { DURABLE_PILOT_ID, PERSISTED_CONVERSATION_FIELDS, eventConversationId, pilotContractError, validatePilotSnapshot } from "./durablePilotContract";
import { emptyMessageMap, patchMessages, conversationMessages, identifierBucket } from "./conversationMessageMap";
import { evictMessagingHistory } from "./evictMessagingHistory";
// Single authoritative canonical store reducer.
const ENTITY_KEYS = Object.freeze({
  conversation: "conversations",
  message: "messages",
  participant: "participants",
  user: "users",
  reaction: "reactions",
  attachment: "attachments",
  channel_account: "channelAccounts",
});

const SUCCESS_STATUS_RANK = Object.freeze({
  processing: 0,
  submitted: 1,
  sent: 2,
  delivered: 3,
  read: 4,
});

const MAX_SEEN_EVENTS = 2000;
const MAX_CONTRACT_ERRORS = 100;

export function createCanonicalMessagingState() {
  return {
    conversations: {},
    messages: emptyMessageMap(),
    participants: {},
    users: {},
    reactions: {},
    attachments: {},
    channelAccounts: {},
    removedConversationIds: {},
    seenEventIds: emptyMessageMap(),
    seenEventOrder: [],
    processedEvents: 0,
    eventTypeCounts: {},
    duplicateEvents: 0,
    ignoredEvents: 0,
    syncRequired: null,
    contractErrors: [],
    lastEventAt: null,
    lastEvent: null,
    recentMessageEvents: [],
    apiHydration: {
      status: "idle",
      views: [],
      viewsByScope: {},
      conversationScopes: {},
      messageScopes: {},
      counts: {},
      errors: [],
      conversationErrors: {},
      startedAt: null,
      completedAt: null,
    },
    conversationOrderByScope: {},
    excludedConversationIdsByScope: {},
    messageOrderByConversation: {},
    liveFieldPaths: {},
    recentViewerStateEvents: [],
  };
}

function uniqueIds(values) {
  return [...new Set((values || []).filter(Boolean))];
}

const optimisticId = clientMessageId => `pending:${clientMessageId}`;

function reconcileOptimisticMessages(state, resources) {
  // Keep presentation-only hints when a full socket/history resource replaces
  // the pending bubble. They are never sent to the server.
  const hints = {};
  const pendingIds = [];
  (resources || []).forEach(item => {
    if (state.messages[item?.id]?._commandAccepted) hints[item.id] = { ...state.messages[item.id], _commandAccepted: false };
    if (!item?.client_message_id) return;
    Object.values(conversationMessages(state.messages, item.conversation_id)).forEach(pending => {
      if (pending.client_message_id !== item.client_message_id || pending.conversation_id !== item.conversation_id
        || pending.id === item.id || (!pending._optimistic && !pending._commandAccepted)) return;
      pendingIds.push(pending.id);
      if (pending._templateHasImageHeader) hints[item.id] = { ...state.messages[item.id], ...hints[item.id], _templateHasImageHeader: true };
      if (pending.context?.reply_to_message_id || pending.context?.reply_to_snapshot) {
        const base = { ...state.messages[item.id], ...hints[item.id] };
        hints[item.id] = { ...base, context: preserveReplyContext(pending.context, base.context || item.context, id => Boolean(id && state.messages[id])) };
      }
    });
  });
  if (!pendingIds.length) return Object.keys(hints).length ? { ...state, messages: patchMessages(state.messages, hints) } : state;
  const removed = new Set(pendingIds);
  const messages = patchMessages(state.messages, hints, pendingIds);
  return { ...state, messages, messageOrderByConversation: Object.fromEntries(
    Object.entries(state.messageOrderByConversation).map(([conversationId, ids]) =>
      [conversationId, ids.filter(id => !removed.has(id))])
  ) };
}

function embeddedResources(state, item, kind) {
  let next = state;
  if (kind === "conversation" && item?.participant?.id) {
    next = { ...next, participants: { ...next.participants, [item.participant.id]: item.participant } };
  }
  if (kind === "message") {
    const reactions = { ...next.reactions };
    (item?.reactions || []).forEach(reaction => {
      if (reaction?.id) reactions[reaction.id] = { ...reaction, message_id: item.id, conversation_id: item.conversation_id };
    });
    const attachment = item?.content?.attachment;
    const attachments = attachment?.id ? { ...next.attachments, [attachment.id]: attachment } : next.attachments;
    next = { ...next, reactions, attachments };
  }
  return next;
}

// A message's quoted target never changes after it is sent. A send receipt or
// live copy that carries no reply data, or only an ID this store can't
// resolve (the live WATI frame quotes by WhatsApp ID, which history maps back
// later), must not erase the quote the bubble already shows. A resolvable ID
// or a snapshot is a verified correction and still wins.
function preserveReplyContext(known, incoming, isLoaded = () => false) {
  const hasReply = context => Boolean(context?.reply_to_message_id || context?.reply_to_snapshot);
  if (!hasReply(known)) return incoming;
  const keep = () => ({ ...(incoming || {}), reply_to_message_id: known.reply_to_message_id ?? null, reply_to_snapshot: known.reply_to_snapshot ?? null });
  if (!hasReply(incoming)) return keep();
  if (!incoming.reply_to_snapshot && incoming.reply_to_message_id !== known.reply_to_message_id
    && !isLoaded(incoming.reply_to_message_id) && (known.reply_to_snapshot || isLoaded(known.reply_to_message_id))) return keep();
  if (!incoming.reply_to_snapshot && known.reply_to_snapshot
    && (!incoming.reply_to_message_id || incoming.reply_to_message_id === known.reply_to_message_id)) {
    return { ...incoming, reply_to_message_id: incoming.reply_to_message_id || known.reply_to_message_id, reply_to_snapshot: known.reply_to_snapshot };
  }
  return incoming;
}

function mergeAssignmentDisplay(current, incoming) {
  if (!incoming || typeof incoming !== "object") return incoming;
  const merged = { ...current, ...incoming };
  if (!hasOwn(incoming, "provider_assignee") && hasOwn(incoming, "assigned_to")) merged.provider_assignee = null;
  return merged;
}

function hydrateItems(state, entityKey, items, kind, conversationReadBaseline) {
  let next = state;
  let entities = entityKey === "messages" ? state.messages : { ...state[entityKey] };
  (items || []).forEach(item => {
    if (!item?.id) return;
    if (entityKey === "conversations" && state.removedConversationIds?.[item.id]) return;
    const current = entities[item.id] || {};
    let merged = { ...current, ...item };
    if (entityKey === "conversations" && hasOwn(item, "assignment")) merged.assignment = mergeAssignmentDisplay(current.assignment, item.assignment);
    if (entityKey === "messages" && hasOwn(item, "context")) merged.context = preserveReplyContext(current.context, item.context, id => Boolean(id && entities[id]));
    // Socket patches received since the last full reset are newer than any
    // in-flight REST page/detail snapshot. Reapply those exact paths after
    // the broad API merge so hydration cannot roll live state backwards.
    // An explicit list reload may supersede patches already present when its
    // request began. A resource changed during the read still keeps its live
    // fields; ordinary background/detail hydration retains the old protection.
    const protectLive = !conversationReadBaseline || current !== conversationReadBaseline[item.id];
    const livePaths = protectLive ? Object.keys(state.liveFieldPaths?.[entityKey]?.[item.id] || {}) : [];
    livePaths.forEach(path => {
      const liveValue = readPath(current, path);
      if (liveValue.found) merged = writePath(merged, path, liveValue.value);
    });
    // A live phone-only name is not newer information than a REST name.
    if (entityKey === "conversations") merged = keepRealParticipantName(keepRealParticipantName(merged, current), item);
    if (entityKey === "messages" && item.status && statusPatchAllowed(current.status, item.status)) {
      // Monotonic REST progress may be newer than a retained socket status.
      merged = { ...merged, status: item.status };
      ["status_updated_at", "failure", "sent_at", "delivered_at", "read_at", "failed_at"].forEach(path => {
        if (hasOwn(item, path)) merged[path] = item[path];
      });
    }
    if (entityKey === "messages" && !statusPatchAllowed(current.status, item.status)) {
      merged = {
        ...merged,
        status: current.status,
        status_updated_at: current.status_updated_at,
        failure: current.failure,
        sent_at: current.sent_at,
        delivered_at: current.delivered_at,
        read_at: current.read_at,
        failed_at: current.failed_at,
      };
    }
    if (entityKey === "messages") entities = patchMessages(entities, { [item.id]: merged });
    else entities[item.id] = merged;
    next = embeddedResources(next, merged, kind);
  });
  return { ...next, [entityKey]: entities };
}

// Recognize only the actual identity's phone fallback, never guess from the
// characters in a nickname. This is hydration precedence, not a display gate.
export function hasRealParticipantName(participant) {
  const name = firstNonBlankText(participant?.display_name);
  return Boolean(name && name !== firstNonBlankText(participant?.identities?.phone));
}

function keepRealParticipantName(entity, known) {
  if (!entity?.participant || hasRealParticipantName(entity.participant) || !hasRealParticipantName(known?.participant)) return entity;
  if (entity.participant.id && known.participant.id && entity.participant.id !== known.participant.id) return entity;
  return { ...entity, participant: { ...entity.participant, display_name: known.participant.display_name } };
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function readPath(source, path) {
  const parts = path.split(".");
  let value = source;
  for (const part of parts) {
    if (!value || typeof value !== "object" || !hasOwn(value, part)) {
      return { found: false, value: undefined };
    }
    value = value[part];
  }
  return { found: true, value };
}

function writePath(target, path, value) {
  const parts = path.split(".");
  const root = { ...target };
  let outputCursor = root;
  let inputCursor = target;

  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      outputCursor[part] = value;
      return;
    }
    const inputChild = inputCursor?.[part];
    const outputChild = inputChild && typeof inputChild === "object" && !Array.isArray(inputChild)
      ? { ...inputChild }
      : {};
    outputCursor[part] = outputChild;
    outputCursor = outputChild;
    inputCursor = inputChild;
  });

  return root;
}

function statusPatchAllowed(currentStatus, incomingStatus) {
  if (!incomingStatus || incomingStatus === currentStatus) return true;
  if (incomingStatus === "failed") return true;
  if (currentStatus === "failed") return false;
  const currentRank = SUCCESS_STATUS_RANK[currentStatus];
  const incomingRank = SUCCESS_STATUS_RANK[incomingStatus];
  if (currentRank === undefined || incomingRank === undefined) return true;
  return incomingRank >= currentRank;
}

function applyChangedFields(current, resource, changedFields) {
  let next = current || {};
  const missing = [];
  const paths = Array.isArray(changedFields) ? changedFields : [];

  for (const path of paths) {
    if (typeof path !== "string" || !path) continue;
    const incoming = readPath(resource, path);
    if (!incoming.found) {
      missing.push(path);
      continue;
    }
    if (path === "status" && !statusPatchAllowed(current?.status, incoming.value)) continue;
    next = writePath(next, path, path === "assignment" ? mergeAssignmentDisplay(current?.assignment, incoming.value) : incoming.value);
  }

  return { next, missing };
}

function rememberEvent(state, eventId) {
  if (!eventId) return state;
  let seenEventIds = patchMessages(state.seenEventIds, { [eventId]: true }, [], identifierBucket(eventId));
  const seenEventOrder = [...state.seenEventOrder, eventId];
  if (seenEventOrder.length > MAX_SEEN_EVENTS) {
    const removed = seenEventOrder.splice(0, seenEventOrder.length - MAX_SEEN_EVENTS);
    seenEventIds = patchMessages(seenEventIds, {}, removed);
  }
  return { ...state, seenEventIds, seenEventOrder };
}

function addContractError(state, error) {
  return {
    ...state,
    contractErrors: [...state.contractErrors, error].slice(-MAX_CONTRACT_ERRORS),
  };
}

function clearConversationReadError(errors = {}, conversationId, resource) {
  if (!errors[conversationId]?.[resource]) return errors;
  const next = { ...errors, [conversationId]: { ...errors[conversationId] } };
  delete next[conversationId][resource];
  if (!Object.keys(next[conversationId]).length) delete next[conversationId];
  return next;
}

function reduceCanonical(state, event) {
  if (event?.type === "messaging/reset") return createCanonicalMessagingState();
  if (event?.type === "messaging/api-started") {
    return { ...state, apiHydration: { ...state.apiHydration, status: "loading", errors: [], startedAt: event.at, completedAt: null } };
  }
  if (event?.type === "messaging/api-restore-scope") {
    return { ...state,
      conversationOrderByScope: { ...state.conversationOrderByScope, [event.scopeKey]: event.ids },
      excludedConversationIdsByScope: { ...state.excludedConversationIdsByScope, [event.scopeKey]: event.excluded },
      apiHydration: { ...state.apiHydration, counts: { ...state.apiHydration.counts, [event.scopeKey]: event.count }, conversationScopes: { ...state.apiHydration.conversationScopes,
        [event.scopeKey]: { ...event.scope, searching_older: false } } },
    };
  }
  if (event?.type === "messaging/api-metadata") {
    const next = event.channelAccounts ? hydrateItems(state, "channelAccounts", event.channelAccounts, "channel_account") : state;
    return { ...next, apiHydration: { ...next.apiHydration, views: event.views || [], viewsByScope: event.viewsByScope || {} } };
  }
  if (event?.type === 'messaging/api-scan-state') {
    const scopes = state.apiHydration.conversationScopes;
    if (!scopes[event.scopeKey]) return state;
    return { ...state, apiHydration: { ...state.apiHydration, conversationScopes: {
      ...scopes, [event.scopeKey]: { ...scopes[event.scopeKey], searching_older: event.searching, scan_paused: event.paused === true },
    } } };
  }
  if (event?.type === "messaging/api-conversations") {
    const next = hydrateItems(state, "conversations", event.items, "conversation", event.conversationReadBaseline);
    const previousOrder = event.append || event.reconcile ? next.conversationOrderByScope[event.scopeKey] || [] : [];
    const previousScope = state.apiHydration.conversationScopes[event.scopeKey];
    const order = event.reconcile ? mergeConversationRefresh(previousOrder, event.items || [], state.conversations, next.conversations)
      : uniqueIds([...previousOrder, ...(event.items || []).map(item => item.id)]);
    return {
      ...next,
      conversationOrderByScope: { ...next.conversationOrderByScope, [event.scopeKey]: order },
      excludedConversationIdsByScope: { ...next.excludedConversationIdsByScope,
        [event.scopeKey]: event.reconcile ? Object.fromEntries([
          ...Object.keys(next.excludedConversationIdsByScope?.[event.scopeKey] || {}),
          ...previousOrder.filter(id => !order.includes(id)),
        ].filter(id => !order.includes(id)).map(id => [id, true])) : event.append ? next.excludedConversationIdsByScope?.[event.scopeKey] || {} : {} },
      apiHydration: {
        ...next.apiHydration,
        conversationScopes: { ...next.apiHydration.conversationScopes, [event.scopeKey]: { params: event.params, ...(event.reconcile && previousScope ? previousScope : event.page) } },
      },
    };
  }
  if (event?.type === "messaging/api-conversation-detail") {
    const next = hydrateItems(state, "conversations", [event.item], "conversation");
    const conversationErrors = clearConversationReadError(next.apiHydration.conversationErrors, event.item.id, "detail");
    return { ...next, apiHydration: { ...next.apiHydration, conversationErrors, detailedConversations: { ...(next.apiHydration.detailedConversations || {}), [event.item.id]: true } } };
  }
  if (event?.type === "messaging/api-messages") {
    const next = hydrateItems(reconcileOptimisticMessages(state, event.items), "messages", event.items, "message");
    const currentOrder = next.messageOrderByConversation[event.conversationId] || [];
    const previousOrder = event.append ? currentOrder : currentOrder.filter(id => next.messages[id]?._optimistic || next.messages[id]?._commandAccepted);
    const orderedIds = uniqueIds([...(previousOrder || []), ...(event.items || []).map(item => item.id)])
      .sort((a, b) => (Date.parse(next.messages[b]?.created_at || next.messages[b]?.occurred_at) || 0)
        - (Date.parse(next.messages[a]?.created_at || next.messages[a]?.occurred_at) || 0));
    return {
      ...next,
      messageOrderByConversation: { ...next.messageOrderByConversation, [event.conversationId]: orderedIds },
      apiHydration: {
        ...next.apiHydration,
        conversationErrors: clearConversationReadError(next.apiHydration.conversationErrors, event.conversationId, "history"),
        messageScopes: { ...next.apiHydration.messageScopes, [event.conversationId]: { ...event.page, loaded_count: orderedIds.filter(id => !next.messages[id]?._optimistic).length } },
      },
    };
  }
  if (event?.type === "messaging/optimistic-start") {
    const { conversationId, clientMessageId, kind, content, file, replyToMessageId, actor } = event;
    if (!conversationId || !clientMessageId || !kind) return state;
    const id = optimisticId(clientMessageId);
    if ((state.messageOrderByConversation[conversationId] || []).some(messageId =>
      messageId !== id && state.messages[messageId]?.client_message_id === clientMessageId)) return state;
    const message = {
      ...(state.messages[id] || {}), id, client_message_id: clientMessageId, conversation_id: conversationId,
      direction: "outbound", kind, content, sender: actor, status: "sending", failure: null,
      created_at: event.queuedAt || new Date().toISOString(), context: { reply_to_message_id: replyToMessageId || null },
      _optimistic: true, _localFile: file || null, _templateHasImageHeader: Boolean(event.templateHasImageHeader),
    };
    return { ...state, messages: patchMessages(state.messages, { [id]: message }),
      messageOrderByConversation: { ...state.messageOrderByConversation,
        [conversationId]: uniqueIds([id, ...(state.messageOrderByConversation[conversationId] || [])]) } };
  }
  if (event?.type === "messaging/optimistic-discarded") {
    const id = optimisticId(event.clientMessageId);
    if (!state.messages[id]?._optimistic) return state;
    return { ...state, messages: patchMessages(state.messages, {}, [id]), messageOrderByConversation: {
      ...state.messageOrderByConversation, [event.conversationId]: (state.messageOrderByConversation[event.conversationId] || []).filter(value => value !== id) } };
  }
  if (event?.type === "messaging/optimistic-queued") {
    const id = optimisticId(event.clientMessageId), item = state.messages[id];
    if (!item?._optimistic) return state;
    return { ...state, messages: patchMessages(state.messages, { [id]: { ...item, status: event.queueState === "failed" ? "failed" : "sending",
      _outboxState: event.queueState, failure: event.error ? { message: event.error, ...(event.errorCode ? { code: event.errorCode } : {}), ...(event.retryAction ? { retry_action: event.retryAction } : {}) } : null } }) };
  }
  if (event?.type === "messaging/optimistic-failed") {
    const id = optimisticId(event.clientMessageId);
    const current = state.messages[id];
    if (!current || !current._optimistic || current.conversation_id !== event.conversationId) return state;
    return { ...state, messages: patchMessages(state.messages, { [id]: { ...current, status: "failed",
      failure: { message: event.error || "Message could not be sent.", ...(event.errorCode ? { code: event.errorCode } : {}), ...(event.retryAction ? { retry_action: event.retryAction } : {}) } } }) };
  }
  if (event?.type === "messaging/optimistic-accepted") {
    const { resource, clientMessageId, conversationId } = event;
    if (!resource?.id?.startsWith("msg_") || resource.client_message_id !== clientMessageId
      || resource.conversation_id !== conversationId) return state;
    const pendingId = optimisticId(clientMessageId);
    const pending = state.messages[pendingId];
    const existing = Object.values(conversationMessages(state.messages, conversationId)).find(item => !item._optimistic
      && item.conversation_id === conversationId && item.client_message_id === clientMessageId);
    const id = existing?.id || resource.id;
    const current = existing || state.messages[id];
    // A receipt from a disposed/previous login must not seed a new session.
    if (!pending && !current) return state;
    if (pending && pending.conversation_id !== conversationId) return state;
    const status = statusPatchAllowed(current?.status, resource.status) ? resource.status : current.status;
    const message = { ...pending, ...resource, ...current, id, status,
      _optimistic: false, _commandAccepted: true, _outboxState: null };
    const loaded = target => Boolean(target && state.messages[target]);
    message.context = preserveReplyContext(current?.context, preserveReplyContext(pending?.context, message.context, loaded), loaded);
    const messages = patchMessages(state.messages, { [id]: message }, [pendingId]);
    return { ...state, messages, messageOrderByConversation: { ...state.messageOrderByConversation,
      [conversationId]: uniqueIds((state.messageOrderByConversation[conversationId] || [id])
        .map(value => value === pendingId ? id : value).concat(id)) } };
  }
  if (event?.type === "messaging/optimistic-star-confirmed") {
    const current = state.conversations[event.conversationId];
    if (!current) return state;
    return { ...state, conversations: { ...state.conversations,
      [event.conversationId]: { ...current, viewer_state: { ...current.viewer_state, starred: event.starred } } } };
  }
  if (event?.type === "messaging/api-count") {
    return { ...state, apiHydration: { ...state.apiHydration, counts: { ...state.apiHydration.counts, [event.scopeKey]: event.value } } };
  }
  if (event?.type === "messaging/api-error") {
    if (event.error?.conversationId) {
      const id = event.error.conversationId;
      const errors = state.apiHydration.conversationErrors || {};
      return { ...state, apiHydration: { ...state.apiHydration, conversationErrors: { ...errors,
        [id]: { ...errors[id], [event.error.resource || "detail"]: event.error } } } };
    }
    return { ...state, apiHydration: { ...state.apiHydration, status: "error", errors: [...state.apiHydration.errors, event.error].slice(-100) } };
  }
  if (event?.type === "messaging/api-completed") {
    return { ...state, apiHydration: { ...state.apiHydration, status: state.apiHydration.errors.length ? "error" : "ready", completedAt: event.at } };
  }
  if (!event || typeof event !== "object" || event.contract_version !== 1) {
    return { ...state, ignoredEvents: state.ignoredEvents + 1 };
  }
  if (event.event_id && state.seenEventIds[event.event_id]) {
    return { ...state, duplicateEvents: state.duplicateEvents + 1 };
  }

  let nextState = rememberEvent(state, event.event_id);
  nextState = {
    ...nextState,
    processedEvents: nextState.processedEvents + 1,
    eventTypeCounts: {
      ...(nextState.eventTypeCounts || {}),
      [event.event_type]: ((nextState.eventTypeCounts || {})[event.event_type] || 0) + 1,
    },
    lastEventAt: event.occurred_at || new Date().toISOString(),
    lastEvent: { id: event.event_id || null, type: event.event_type, resource_type: event.resource_type,
      resource_id: event.resource_id || event.data?.resource?.id || null,
      changed_fields: event.data?.changed_fields || [] },
  };

  if (event.event_type === "messaging.sync.required") {
    return {
      ...nextState,
      syncRequired: { ...(event.data?.resource || { scope: "all" }), event_id: event.event_id || null },
    };
  }

  const entityKey = ENTITY_KEYS[event.resource_type];
  const resource = event.data?.resource;
  const resourceId = event.resource_id || resource?.id;
  if (!entityKey || !resourceId || !resource || typeof resource !== "object") {
    return addContractError(nextState, {
      event_id: event.event_id || null,
      event_type: event.event_type || null,
      reason: "invalid_resource_envelope",
    });
  }

  if (entityKey === "messages" && event.event_type === "messaging.message.created") {
    nextState = reconcileOptimisticMessages(nextState, [resource]);
    nextState = { ...nextState, recentMessageEvents: [...(nextState.recentMessageEvents || []),
      { id: event.event_id, message_id: resourceId }].slice(-100) };
  }

  if (event.event_type === "messaging.conversation.removed"
    && event.resource_type === "conversation"
    && resource.removal_scope === "supplier_list_association") {
    const conversations = { ...nextState.conversations };
    delete conversations[resourceId];
    return {
      ...nextState,
      conversations,
      removedConversationIds: { ...nextState.removedConversationIds, [resourceId]: true },
      conversationOrderByScope: Object.fromEntries(Object.entries(nextState.conversationOrderByScope)
        .map(([scope, ids]) => [scope, ids.filter(id => id !== resourceId)])),
    };
  }
  if (entityKey === "conversations" && nextState.removedConversationIds?.[resourceId]) return nextState;

  if (event.event_type === "messaging.message.reaction_changed" && event.resource_type === "reaction") {
    const current = nextState.reactions[resourceId] || {
      id: resourceId,
      message_id: resource.message_id ?? null,
      conversation_id: resource.conversation_id ?? null,
    };
    const { next, missing } = applyChangedFields(current, resource, event.data?.changed_fields);
    if (missing.length) {
      nextState = addContractError(nextState, {
        event_id: event.event_id || null,
        event_type: event.event_type || null,
        reason: "changed_field_missing_from_resource",
        fields: missing,
      });
    }
    const reactions = { ...nextState.reactions };
    if (resource.operation === "remove") delete reactions[resourceId];
    else reactions[resourceId] = { ...next, id: resourceId };
    return { ...nextState, reactions };
  }

  const current = nextState[entityKey][resourceId] || { id: resourceId };
  const incomingStatus = readPath(resource, "status");
  if (
    event.event_type === "messaging.message.status_changed"
    && incomingStatus.found
    && !statusPatchAllowed(current.status, incomingStatus.value)
  ) {
    return nextState;
  }
  const { next, missing } = applyChangedFields(current, resource, event.data?.changed_fields);
  let entity = { ...next, id: resourceId };
  if (entityKey === "conversations") entity = keepRealParticipantName(entity, current);
  if (entityKey === "messages") entity.context = preserveReplyContext(current.context, entity.context, id => Boolean(id && nextState.messages[id]));
  // Nested verdict patches may omit the attachment ID. Use the fully merged
  // resource so its descriptor cache and the existing bubble stay consistent.
  if (entityKey === "messages" && (event.data?.changed_fields || []).some(path => path.startsWith("content.attachment."))) {
    nextState = embeddedResources(nextState, entity, "message");
  }
  if (entityKey === "conversations" && entity.assignment && (event.data?.changed_fields || []).includes("assignment.assigned_to")
    && !(event.data?.changed_fields || []).some(path => path === "assignment" || path.startsWith("assignment.provider_assignee"))) {
    entity.assignment = { ...entity.assignment, provider_assignee: null };
  }
  nextState = {
    ...nextState,
    [entityKey]: entityKey === "messages" ? patchMessages(nextState.messages, { [resourceId]: entity }) : { ...nextState[entityKey], [resourceId]: entity },
    liveFieldPaths: {
      ...nextState.liveFieldPaths,
      [entityKey]: entityKey === "messages" ? patchMessages(nextState.liveFieldPaths.messages, {
        [resourceId]: { ...nextState.liveFieldPaths.messages?.[resourceId],
          ...(event.data?.changed_fields || []).reduce((paths, path) => { if (typeof path === "string" && path) paths[path] = true; return paths; }, {}) },
      }, [], entity.conversation_id) : {
        ...(nextState.liveFieldPaths?.[entityKey] || {}),
        [resourceId]: {
          ...(nextState.liveFieldPaths?.[entityKey]?.[resourceId] || {}),
          ...(entityKey === "conversations" && (event.data?.changed_fields || []).includes("assignment.assigned_to") ? { "assignment.provider_assignee": true } : {}),
          ...(event.data?.changed_fields || []).reduce((paths, path) => {
            if (typeof path === "string" && path) paths[path] = true;
            return paths;
          }, {}),
        },
      },
    },
  };
  if (event.event_type === "messaging.conversation.viewer_state_changed") {
    nextState = {
      ...nextState,
      recentViewerStateEvents: [
        ...(nextState.recentViewerStateEvents || []),
        {
          event_id: event.event_id || null,
          conversation_id: resourceId,
          unread_count: resource.viewer_state?.unread_count ?? null,
          reason: resource.reason ?? null,
          occurred_at: event.occurred_at || null,
        },
      ].slice(-20),
    };
  }
  if (entityKey === "messages" && event.event_type === "messaging.message.created" && entity.conversation_id) {
    // A message can arrive before its companion conversation patch. Keep its
    // parent discoverable for targeted hydration, even after the recent-event
    // diagnostic window rolls over. No unknown fields are fabricated here.
    nextState = { ...nextState, liveFieldPaths: { ...nextState.liveFieldPaths,
      conversations: { ...nextState.liveFieldPaths?.conversations,
        [entity.conversation_id]: nextState.liveFieldPaths?.conversations?.[entity.conversation_id] || {} } } };
    const previous = nextState.messageOrderByConversation[entity.conversation_id] || [];
    const ordered = uniqueIds([resourceId, ...previous]).sort((leftId, rightId) => {
      const left = messageTimeMs(nextState.messages[leftId]?.created_at) ?? -Infinity;
      const right = messageTimeMs(nextState.messages[rightId]?.created_at) ?? -Infinity;
      return right - left;
    });
    nextState = { ...nextState, messageOrderByConversation: { ...nextState.messageOrderByConversation, [entity.conversation_id]: ordered } };
  }

  if (missing.length) {
    nextState = addContractError(nextState, {
      event_id: event.event_id || null,
      event_type: event.event_type || null,
      reason: "changed_field_missing_from_resource",
      fields: missing,
    });
  }
  return nextState;
}

function messageTimeMs(value) {
  if (value === null || value === undefined || value === "") return null;
  const text = String(value);
  const numeric = Number(value);
  const date = Number.isFinite(numeric) && /^\d+(?:\.\d+)?$/.test(text)
    ? new Date(numeric > 1e12 ? numeric : numeric * 1000)
    : new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

function clearPilotLivePaths(state) {
  const liveFieldPaths = { ...state.liveFieldPaths, conversations: { ...state.liveFieldPaths.conversations }, messages: { ...state.liveFieldPaths.messages } };
  delete liveFieldPaths.conversations[DURABLE_PILOT_ID];
  Object.values(state.messages).forEach(item => {
    if (item.conversation_id === DURABLE_PILOT_ID) delete liveFieldPaths.messages[item.id];
  });
  return { ...state, liveFieldPaths };
}

function installPilotPage(state, conversation, messages) {
  let next = clearPilotLivePaths(state);
  // Remove the previous page's persisted resources, retaining unsent commands.
  next = { ...next, messages: Object.fromEntries(Object.entries(next.messages).filter(([, item]) =>
    item.conversation_id !== DURABLE_PILOT_ID || item._optimistic)),
  reactions: Object.fromEntries(Object.entries(next.reactions).filter(([, item]) => item.conversation_id !== DURABLE_PILOT_ID)) };
  next = reduceCanonical(next, { type: "messaging/api-conversation-detail", item: conversation });
  next = reduceCanonical(next, { type: "messaging/api-messages", conversationId: DURABLE_PILOT_ID,
    items: messages.items, page: { ...messages.page, history: messages.history }, append: false });
  return next;
}

export function canonicalMessagingReducer(state, action) {
  if (action?.type === "messaging/evict-history") return evictMessagingHistory(state, action.openIds, action.recentIds);
  if (action?.type === "messaging/customer-type") {
    const target = state.conversations[action.conversationId];
    const customerId = action.customerId ?? target?.business_context?.customer_id;
    let conversations = state.conversations;
    Object.values(state.conversations).forEach(conversation => {
      const sameCustomer = customerId != null && conversation.business_context?.customer_id != null
        && String(conversation.business_context.customer_id) === String(customerId);
      if (!sameCustomer && conversation.id !== action.conversationId) return;
      if (conversation.business_context?.customer_type === action.customerType) return;
      if (conversations === state.conversations) conversations = { ...conversations };
      conversations[conversation.id] = { ...conversation, business_context: {
        ...conversation.business_context, customer_type: action.customerType,
      } };
    });
    return conversations === state.conversations ? state : { ...state, conversations };
  }
  if (action?.type === "messaging/api-link-preview") {
    const current = state.messages[action.messageId];
    if (!current || !["content.link_preview", "referral.link_preview"].includes(action.path)) return state;
    const preview = readPath(current, action.path);
    if (preview.value?.availability !== "pending" || preview.value?.url !== action.url) return state;
    return { ...state, messages: patchMessages(state.messages, { [current.id]: writePath(current, action.path, action.preview) }) };
  }
  if (!action || typeof action !== "object") return reduceCanonical(state, action);
  if (action.type === "messaging/pilot-snapshot") {
    validatePilotSnapshot(action.snapshot);
    if (action.snapshot.recovery.state !== "active") return state;
    const next = installPilotPage(state, action.snapshot.conversation, action.snapshot.messages);
    return { ...next, durablePilot: { active: true, cursor: action.snapshot.recovery.cursor, seen: {} } };
  }
  if (action.type === "messaging/pilot-rollback") {
    const next = installPilotPage(state, action.conversation, action.messages);
    return { ...next, durablePilot: null };
  }
  if (action.type === "messaging/pilot-replay") {
    if (!state.durablePilot?.active || state.durablePilot.cursor !== action.previousCursor) throw pilotContractError("Replay checkpoint changed before application.");
    let next = state;
    const seen = { ...state.durablePilot.seen };
    for (const event of action.items) {
      if (event?.contract_version !== 1 || event?.durability !== "durable" || !event.event_id
        || eventConversationId(event, next) !== DURABLE_PILOT_ID) throw pilotContractError("Invalid or incorrectly scoped replay event.");
      if (seen[event.event_id]) continue;
      // Compatibility delivery before activation is not a replay checkpoint.
      next = { ...next, seenEventIds: { ...next.seenEventIds, [event.event_id]: false } };
      const errors = next.contractErrors;
      next = reduceCanonical(next, event);
      if (next.contractErrors !== errors) throw pilotContractError("Replay event has an invalid resource or changed field.");
      seen[event.event_id] = true;
    }
    return { ...next, durablePilot: { ...state.durablePilot, cursor: action.cursor, seen } };
  }
  if (action.type === "messaging/pilot-history") {
    if (!state.durablePilot?.active) return state;
    const next = reduceCanonical(state, { ...action, type: "messaging/api-messages", conversationId: DURABLE_PILOT_ID });
    // A revision-reset page may race with replayed messages. Preserve those
    // newer rows, just as hydrateItems preserves their live field values.
    if (action.append) return next;
    const liveIds = (state.messageOrderByConversation[DURABLE_PILOT_ID] || []).filter(id => state.liveFieldPaths.messages?.[id]);
    return { ...next, messageOrderByConversation: { ...next.messageOrderByConversation,
      [DURABLE_PILOT_ID]: uniqueIds([...next.messageOrderByConversation[DURABLE_PILOT_ID], ...liveIds]) } };
  }
  if (state.durablePilot?.active) {
    if (action.type === "messaging/api-messages" && action.conversationId === DURABLE_PILOT_ID) return state;
    const enrichmentOnly = item => item?.id !== DURABLE_PILOT_ID ? item : Object.fromEntries(
      Object.entries(item).filter(([key]) => key === "id" || !PERSISTED_CONVERSATION_FIELDS.has(key)));
    if (action.type === "messaging/api-conversations") action = { ...action, items: action.items.map(enrichmentOnly) };
    if (action.type === "messaging/api-conversation-detail") action = { ...action, item: enrichmentOnly(action.item) };
    if (action.contract_version === 1 && eventConversationId(action, state) === DURABLE_PILOT_ID
      && !/typing|presence/.test(action.event_type || "")) return state;
  }
  return reduceCanonical(state, action);
}
